import { sampleColormap } from '@/lib/colormaps';
import { defaultGradientName, rampPosition } from '@/lib/cutFillConvention';
import * as THREE from 'three';

/**
 * How a surface-difference GLB is coloured in the viewer.
 *
 * The worker bakes RGB vertex colours from the run's colormap and range AND
 * stores the difference itself per vertex (glTF `_DZ`, surfaced by
 * GLTFLoader as `geometry.attributes._dz`). With that attribute the viewer
 * can repaint the bodies from the layer settings — the same "display range"
 * a deviation cloud has, plus a colormap of its own — without a re-run. A
 * GLB from before the attribute existed keeps its baked colours.
 */

/**
 * Matplotlib colormap name (what the run dialog sends) → viewer PALETTE.
 *
 * Palette only. Both orientations of a diverging ramp land on the same row,
 * because the target is a `PointColorType` (`graphql/layer.gql`) — an
 * enumeration of palettes, with no reversed members — and because orientation
 * is not a property of a palette anyway. Which end is cut is decided at paint
 * time by `rampPosition`, per kind of run, so the one stored `SPECTRAL` paints
 * a deviation's positive side red and a surface difference's negative side
 * red.
 *
 * (An earlier attempt at this added a reversed `SPECTRAL_INV` palette. It
 * would have failed enum validation on save and silently discarded the user's
 * whole layer-settings object.)
 *
 * `viridis_r` lands on the same `VIRIDIS` row as plain `viridis` for the same
 * reason: it is a REVERSED SEQUENTIAL spelling (`colormaps.ts`'s
 * `MATPLOTLIB_RAMP_OPTIONS` now sends it for a surface-difference run, so the
 * bake's low-dz/cut end lands on viridis's strong end instead of its weak
 * one), not a different palette.
 */
export const MATPLOTLIB_TO_GRADIENT: Record<string, string> = {
  Spectral: 'SPECTRAL',
  Spectral_r: 'SPECTRAL',
  RdBu: 'SPECTRAL',
  RdBu_r: 'SPECTRAL',
  coolwarm: 'SPECTRAL',
  coolwarm_r: 'SPECTRAL',
  viridis: 'VIRIDIS',
  viridis_r: 'VIRIDIS',
  jet: 'RAINBOW',
};

export const DZ_ATTRIBUTE = '_dz';

export interface SurfaceDifferenceShading {
  gradient: string;
  min: number;
  max: number;
  /** True when either end of the range comes from the layer settings rather than the run. */
  overridden: boolean;
}

/**
 * The gradient and range to paint (and to draw in the legend) for a layer:
 * the settings' colormap, and the settings' display range where set, the
 * run's own range otherwise. `meta.range` absent → ±1 as the panel always
 * assumed.
 */
export function resolveSurfaceDifferenceShading(
  settings:
    | { surfaceDifferenceColormap?: string | null; scalarRangeMin?: number | null; scalarRangeMax?: number | null }
    | null
    | undefined,
  meta: { range?: [number, number] | null; colormap?: string | null } | null | undefined,
): SurfaceDifferenceShading {
  const [runMin, runMax] = meta?.range ?? [-1, 1];
  const min = settings?.scalarRangeMin ?? runMin;
  const max = settings?.scalarRangeMax ?? runMax;
  // The palette only. A run whose colormap we cannot read still gets the
  // convention's default palette, and the CUT SIDE of it is `rampPosition`'s
  // business either way.
  const gradient =
    settings?.surfaceDifferenceColormap ??
    MATPLOTLIB_TO_GRADIENT[meta?.colormap ?? ''] ??
    defaultGradientName('surfaceDifference');
  return {
    gradient,
    min,
    max,
    overridden: settings?.scalarRangeMin != null || settings?.scalarRangeMax != null,
  };
}

/** Does any mesh under `root` carry the per-vertex difference? */
export function hasVertexDz(root: THREE.Object3D): boolean {
  let found = false;
  root.traverse((o) => {
    const g = (o as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
    if (g?.getAttribute(DZ_ATTRIBUTE)) found = true;
  });
  return found;
}

/**
 * Rewrite `geometry`'s colour attribute from its `_dz` attribute. Returns
 * false (and touches nothing) when the geometry has no `_dz` — a GLB made
 * before the attribute existed keeps the colours the worker baked. Values
 * outside [min, max] clamp to the ends, as the worker's own ramp does.
 *
 * `gradient` is a PALETTE; the cut/fill orientation comes from
 * `rampPosition('surfaceDifference', …)`, so a GLB baked by ANY older run is
 * repainted cut-red as soon as the viewer touches it.
 */
export function recolorByDz(geometry: THREE.BufferGeometry, gradient: string, min: number, max: number): boolean {
  const dz = geometry.getAttribute(DZ_ATTRIBUTE) as THREE.BufferAttribute | undefined;
  if (!dz) return false;
  const count = dz.count;
  const existing = geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
  // Reuse the buffer when it is already a 3-float colour of the right size —
  // the baked colour from the GLB is usually a normalised uint8 RGBA, which is
  // replaced once and then updated in place on every later change.
  const reuse =
    existing && existing.itemSize === 3 && existing.count === count && existing.array instanceof Float32Array;
  const colors = reuse ? (existing.array as Float32Array) : new Float32Array(count * 3);
  const c = new THREE.Color();
  for (let i = 0; i < count; i++) {
    // NOT `(dz - min) / span`. `dz = top - bottom`, so the CUT side is the
    // negative one, and the convention puts cut at the palette's HIGH end
    // (`@/lib/cutFillConvention`) — which is what makes a single stored
    // `SPECTRAL` paint cut red here and on a deviation cloud alike, and what
    // makes a single-hue palette the user picked still put cut at its strong
    // end. `rampPosition` clamps, as this loop always did.
    sampleColormap(gradient, rampPosition('surfaceDifference', dz.getX(i), min, max), c);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  if (reuse) {
    existing.needsUpdate = true;
  } else {
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  }
  return true;
}
